import React, { useContext } from 'react';

import {
  Box,
  Button,
  CircularProgress,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';

import { DeviceContext } from '@vyce/core/src/contexts';
import { AppCheckbox } from '@vyce/core/src/components/inputs';
import { ButtonTitleWithLoading } from '@vyce/core/src/components/ButtonTitleWithLoading';
import { formatDate } from '@vyce/core/src/utils/dates';
import { CheckInWorkerDTO } from '@vyce/core/src/api/types';

import { MAIN_CONTAINER_ID } from '../../../constants';
import { bulkSkipReasonText } from '../config/constants';
import { BulkClockOutRequestParams, BulkClockOutResult } from '../hooks/useBulkClockOut';

const id = 'bulk-clock-out-dialog-title';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    content: {
      [theme.breakpoints.up('sm')]: {
        minWidth: 520,
      },
    },
    summary: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
      marginBottom: theme.spacing(2),
    },
    list: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 8,
      maxHeight: 400,
      overflowY: 'auto',
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(0.5, 1.5, 0.5, 0.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
      cursor: 'pointer',
      '&:last-child': {
        borderBottom: 'none',
      },
    },
    headerRow: {
      backgroundColor: theme.palette.background.default,
    },
    rowText: {
      flex: 1,
      minWidth: 0,
    },
    bold: {
      fontWeight: 600,
    },
    skippedReason: {
      color: theme.palette.text.secondary,
      whiteSpace: 'nowrap',
    },
    center: {
      display: 'flex',
      justifyContent: 'center',
      padding: theme.spacing(4),
    },
  })
);

interface Props {
  isOpen: boolean;
  params: BulkClockOutRequestParams | null;
  workers: CheckInWorkerDTO[];
  selectedIds: Set<string>;
  loading: boolean;
  submitting: boolean;
  result: BulkClockOutResult | null;
  onClose: () => void;
  onToggleWorker: (userId: string) => void;
  onToggleAll: () => void;
  onConfirm: () => void;
}

// The row is the click target; the checkbox handles its own click so it doesn't toggle twice.
const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();

const timeLabel = (timestamp: BulkClockOutRequestParams['timestamp']) =>
  timestamp instanceof Date ? formatDate(timestamp, 'HH:mm') : 'Right now';

export const BulkClockOutDialog = ({
  isOpen,
  params,
  workers,
  selectedIds,
  loading,
  submitting,
  result,
  onClose,
  onToggleWorker,
  onToggleAll,
  onConfirm,
}: Props) => {
  const classes = useStyles();
  const { isMobile } = useContext(DeviceContext);
  const selectedCount = selectedIds.size;
  const everyoneTicked = !!workers.length && selectedCount === workers.length;

  const renderPreview = () => {
    if (loading) {
      return (
        <Box className={classes.center}>
          <CircularProgress />
        </Box>
      );
    }
    if (!workers.length) {
      return <Typography>Nobody is clocked in on the selected locations right now.</Typography>;
    }
    return (
      <Box className={classes.list}>
        <Box className={`${classes.row} ${classes.headerRow}`} onClick={onToggleAll}>
          <AppCheckbox
            variant="rectangle"
            color="primary"
            checked={everyoneTicked}
            indeterminate={!everyoneTicked && selectedCount > 0}
            onClick={stopPropagation}
            onChange={onToggleAll}
            inputProps={{ 'aria-label': 'Select everyone' }}
          />
          <Typography className={`${classes.rowText} ${classes.bold}`}>
            {selectedCount} of {workers.length} selected
          </Typography>
        </Box>
        {workers.map(worker => (
          <Box key={worker.user_id} className={classes.row} onClick={() => onToggleWorker(worker.user_id)}>
            <AppCheckbox
              variant="rectangle"
              color="primary"
              checked={selectedIds.has(worker.user_id)}
              onClick={stopPropagation}
              onChange={() => onToggleWorker(worker.user_id)}
              inputProps={{ 'aria-label': `${worker.first_name} ${worker.last_name}` }}
            />
            <Box className={classes.rowText}>
              <Typography noWrap>
                {worker.first_name} {worker.last_name}
              </Typography>
              <Typography variant="caption" color="textSecondary" noWrap component="p">
                {worker.site_name}
                {worker.checked_in ? ` · in since ${formatDate(worker.checked_in, 'HH:mm')}` : ''}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const renderResult = (result: BulkClockOutResult) => (
    <>
      <Typography variant="h6" className={classes.bold}>
        Clocked out {result.clockedOutCount}
      </Typography>
      {!!result.skipped.length && (
        <>
          <Typography color="textSecondary" style={{ marginTop: 16, marginBottom: 8 }}>
            {result.skipped.length} left as they were:
          </Typography>
          <Box className={classes.list}>
            {result.skipped.map(item => (
              <Box key={item.userId} className={classes.row} style={{ cursor: 'default', paddingLeft: 12 }}>
                <Typography className={classes.rowText} noWrap>
                  {item.name}
                </Typography>
                <Typography variant="body2" className={classes.skippedReason}>
                  {bulkSkipReasonText[item.reason] ?? item.reason}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
    </>
  );

  return (
    <Dialog
      container={document.getElementById(MAIN_CONTAINER_ID)}
      fullScreen={isMobile}
      open={isOpen}
      onClose={submitting ? undefined : onClose}
      aria-labelledby={id}>
      <DialogTitle id={id}>{result ? 'Done!' : 'Clock out all clocked-in users'}</DialogTitle>
      <DialogContent className={classes.content}>
        {!result && params && (
          <Box className={classes.summary}>
            <Typography variant="body2" color="textSecondary">
              Locations: <strong>{params.locationsLabel}</strong>
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Clock-out time: <strong>{timeLabel(params.timestamp)}</strong>
            </Typography>
          </Box>
        )}
        {result ? renderResult(result) : renderPreview()}
      </DialogContent>
      <DialogActions>
        {result ? (
          <Button size="large" color="primary" variant="contained" style={{ minWidth: 115 }} onClick={onClose}>
            OK
          </Button>
        ) : (
          <Box width="100%" display="flex" justifyContent="space-between">
            <Button size="large" variant="outlined" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              size="large"
              color="primary"
              variant="contained"
              style={{ minWidth: 115 }}
              disabled={loading || submitting || !selectedCount}
              onClick={onConfirm}>
              <ButtonTitleWithLoading
                title={`Clock out ${selectedCount}`}
                loaderVariant="paper"
                loading={submitting}
              />
            </Button>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
};
